import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getMachineDetailsPositionColumns } from './columns/MachineDetailsPosition.columns';
import { MachinePositionHistoryMap } from 'app/modules/machine-inventory/components/MachinePositionHistoryMap/MachinePositionHistoryMap';
import { MACHINE_POSITION_HISTORY_PAGE_SIZE } from 'app/modules/machine-inventory/interfaces/MachinePosition.types';
import { MachineDetailsPanelActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelActions';
import {
  selectPositionHistoriesData,
  selectPositionHistoriesIsLoading,
  selectPositionHistoriesNextPaginationToken,
  selectPositionHistoriesRequestParams,
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelSelectors';
import { KCCThemeGlobalStyles } from 'config/theme/kccTheme/globalStyles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { InfiniteTable } from 'lib/components/InfiniteTable/InfiniteTable';

export interface MachineDetailsPositionProps {
  machineId: string;
}

const disabledFutureDate = (current: Dayjs): boolean => !!current && current.isAfter(dayjs().endOf('day'));

export const MachineDetailsPosition = ({ machineId }: MachineDetailsPositionProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState({
    startAt: dayjs().subtract(1, 'year'),
    endAt: dayjs().endOf('day'),
  });

  const equipmentPositionSummaries = useSelector(selectPositionHistoriesData) || [];
  const isLoading = !!useSelector(selectPositionHistoriesIsLoading);
  const nextPaginationToken = useSelector(selectPositionHistoriesNextPaginationToken);
  const requestParams = useSelector(selectPositionHistoriesRequestParams);

  const handleOnChangeDate = useCallback((value: [Dayjs | null, Dayjs | null] | null): void => {
    if (value?.[0] && value[1]) {
      setDateRange({
        startAt: value[0],
        endAt: value[1].endOf('day'),
      });
      return;
    }

    setDateRange({
      startAt: dayjs().subtract(1, 'year'),
      endAt: dayjs().endOf('day'),
    });
  }, []);

  useEffect(() => {
    dispatch(
      MachineDetailsPanelActions.getMachinePositionHistoriesRequest({
        machineId,
        period: {
          startAt: dateRange.startAt.startOf('day').toISOString(),
          endAt: dateRange.endAt.endOf('day').toISOString(),
        },
        paginationOptions: {
          limit: MACHINE_POSITION_HISTORY_PAGE_SIZE,
          paginationToken: '',
        },
      })
    );
  }, [dateRange.endAt, dateRange.startAt, dispatch, machineId]);

  const loadMore = useCallback((): void => {
    if (!requestParams || !nextPaginationToken) {
      return;
    }

    dispatch(
      MachineDetailsPanelActions.getMachinePositionHistoriesMoreDataRequest({
        ...requestParams,
        paginationOptions: {
          limit: MACHINE_POSITION_HISTORY_PAGE_SIZE,
          paginationToken: nextPaginationToken,
        },
      })
    );
  }, [dispatch, nextPaginationToken, requestParams]);

  const columns = useMemo(() => getMachineDetailsPositionColumns({ t }), [t]);

  return (
    <>
      <KCCThemeGlobalStyles />

      <Flex vertical gap={kccThemeConstants.SPACING.LG}>
        <DatePicker.RangePicker
          allowClear={false}
          value={[dateRange.startAt, dateRange.endAt]}
          onChange={handleOnChangeDate}
          disabledDate={disabledFutureDate}
        />

        <MachinePositionHistoryMap machinePositionSummaries={equipmentPositionSummaries} isLoading={isLoading} />

        <InfiniteTable
          dataSource={equipmentPositionSummaries}
          columns={columns}
          next={loadMore}
          hasMore={!!nextPaginationToken}
          height={400}
          isLoading={isLoading}
        />
      </Flex>
    </>
  );
};
